import Vue from 'vue';
import Vuex from 'vuex';
import global from 'store/global';
import cart from 'store/cart';
import product from 'store/product';
import quickView from 'store/quick-view';
import shadeFinder from 'store/shade-finder';
import vto from 'store/vto';

import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import {
  trackProductClick,
  buildItemTrackingData,
  setLocalStorageProperties,
  trackNotifyMeBtnClicked
} from 'lib/tracking';
import {
  convertGQLToProductJson,
  encodeObjectID,
  htmlDecode,
  formatPrice,
} from 'utils/general';
import { trackEvent } from '../lib/tracking';

Vue.use(Vuex);
window.KENDO = {};

const defineGlobals = (store) => {
  /**
   * Retrieves properties for a given product variant
   * used for cart promo/bundle message rendering
   */
  const getProperties = async (atcParams) => {
    try {
      const properties = {};
      const globalVariantId = encodeObjectID(atcParams.id, 'ProductVariant');
      const response = await storefront.request(gqlQueries.getPropertiesById, {
        id: globalVariantId,
      });

      response.node.product.metafields
        .filter((metafield) => metafield)
        .forEach((metafield) => {
          switch (metafield.key) {
            case 'cart_upsell_bundle':
              properties._upsell_bundle = metafield.value;
              break;
            case 'link':
              properties._promo_link = metafield.value;
              break;
            case 'reviews_average':
              properties._product_rating = metafield.value;
              break;
            default:
              properties[`_${metafield.key}`] = metafield.value;
              break;
          }
        });

      return properties;
    } catch (error) {
      console.error(error);
      return {};
    }
  };

  window.KENDO.addToCart = async (atcParams, index = 0, attributes = null) => {
    const properties = attributes || await getProperties(atcParams)

    store.dispatch('cart/addToCart', {
      ...atcParams,
      properties: {
        ...properties,
        _index: index + 1,
      },
    });
  };

  const openBackInStock = (bisParams) => {
    store.dispatch('global/updateBackInStock', bisParams);
    store.dispatch('global/openModal', 'back-in-stock');
  };

  const openQuickModal = (product, variantId = null) => {
    store.dispatch('quickView/updateModalTitle', 'Quick shop');
    store.dispatch('quickView/updateCtaText', 'Add to bag');
    store.dispatch('quickView/setProduct', product);
    if (variantId) store.dispatch('quickView/setVariant', variantId);
    store.dispatch('global/openModal', 'quick-view');
  };

  const getProductData = (handle) => {
    return fetch(`${Shopify.routes.root}products/${handle}?view=json`)
      .then((response) => response.text())
      .then((response) => {
        const product = JSON.parse(response);
        return product;
      })
      .catch(() => {
        console.warn(`Error fetching data from /products/${handle}`);
      });
  };

  window.KENDO.openQuickModal = (product) => {
    if (typeof product === 'string') {
      product = JSON.parse(decode(product));
    }
    openQuickModal(product);
  };

  window.KENDO.openBackInStock = (bisParams) => {
    openBackInStock(bisParams);
    trackNotifyMeBtnClicked(bisParams.product.title, 'notify_me');
  };

  window.KENDO.openQuickModalByHandle = (handle, variantId) => {
    getProductData(handle).then((product) => openQuickModal(product, variantId));
  };

  window.KENDO.openBackInStockByHandle = (handle) => {
    getProductData(handle).then((product) => {
      openBackInStock({
        product: {
          id: product.id,
          title: product.title,
          price: product.price,
          featuredImage: product.featuredImage,
        },
        variant: product.variants[0],
      })
      trackNotifyMeBtnClicked(product.title, window.searchspring.controller?.search?.context?.collection?.name || 'Search');
    });
  };

  /**
   * Get details of a it's children
   *
   * @param {Array} array Handles of child products within a bundle : product.metafields.accentuate.bundled_products
   *
   * @return {Object} Query response of child products in a bundle
   */
  window.KENDO.requestBundleData = (array) => {
    return Promise.resolve(array)
      .then((response) => {
        const productinfoPromises = [];
        for (let i = 0; i < response.length; i++) {
          const p = Promise.resolve()
            .then((data) => {
              let handle = response[i];
              if (handle.includes('|')) {
                handle = handle.split('|')[0];
              }
              return storefront.request(gqlQueries.getProductIdByHandle, {
                handle: handle,
              });
            })
            .then((response) => {
              if (response.product) {
                const id = response.product.id;
                return { id: id };
              } else {
                return false;
              }
            });
          productinfoPromises.push(p);
        }
        return Promise.all(productinfoPromises).then((values) => {
          const idList = values.map((p) => p.id).filter((p) => p);
          return storefront.request(gqlQueries.productsPrices, { ids: idList });
        });
      })
      .then((response) => {
        return response;
      });
  };

  window.KENDO.calculateBundleDiscount = (bundleResponse, productPrice) => {
    if (!bundleResponse || !productPrice) return;

    const totalBundlePrice = bundleResponse.nodes.reduce(
      (price, product) =>
        parseInt(price) + parseInt(product.variants.edges[0].node.price),
      0,
    );
    const priceDiff = totalBundlePrice - parseInt(productPrice) / 100;
    let bundleDiscountAmount = priceDiff / bundleResponse.nodes.length;
    bundleDiscountAmount = bundleDiscountAmount.toFixed(2);

    return bundleDiscountAmount;
  };

  const getBundleData = async (productHandle) => {
    if (!productHandle) return;
    let tempProductSlug = '';
    const bundleItems = [];
    let storefrontResponse = await storefront.request(gqlQueries.product, {
      handle: productHandle,
    });
    const productData = convertGQLToProductJson(storefrontResponse.product);
    if (!productData.isBundle) return null;

    const bundleProductHandles =
      productData.metafields.accentuate.bundled_products.map(
        (handle) => handle.split('|')[0],
      );
    await Promise.all(
      bundleProductHandles.map(async (productSlug, i) => {
        if (productSlug.includes('|')) {
          tempProductSlug = productSlug.split('|');
          storefrontResponse = await storefront.request(gqlQueries.product, {
            handle: tempProductSlug[0],
          });
        } else {
          storefrontResponse = await storefront.request(gqlQueries.product, {
            handle: productSlug,
          });
        }
        bundleItems[i] = convertGQLToProductJson(storefrontResponse.product);
      }),
    );

    return bundleItems;
  };

  /**
   * Calculate the sum total of all child products in a bundle
   *
   * @param {String} productHandle Handle of the bundle product
   *
   * @return {Number} Sum total of all child products in a bundle
   */
  window.KENDO.getTotalBundleValue = async (productHandle) => {
    if (!productHandle) return;
    const bundleItems = await getBundleData(productHandle);
    if (!bundleItems) return;
    const totalValue = bundleItems
      .filter((item) => item)
      .reduce(
        (total, item) =>
          (total += parseInt(
            item.compareAtPrice > item.price ? item.compareAtPrice : item.price,
            10,
          )),
        0,
      );

    return formatPrice(totalValue, true);
  };

  window.KENDO.checkBundleAvailability = async (productHandle) => {
    const bundleItems = await getBundleData(productHandle);
    if (!bundleItems) {
      return false;
    }

    const bundleRestrictedProducts = bundleItems.filter((bundleProduct) =>
      (bundleProduct.tags ?? []).some((tag) =>
        tag.includes('shipping-restricted-replacement'),
      ),
    );

    if (bundleRestrictedProducts.length === 0) {
      return false;
    }

    for (const bundleProduct of bundleRestrictedProducts) {
      const restrictedProductTag = bundleProduct.tags?.find((tag) =>
        tag.includes(`shipping-restricted-replacement|${window.shippingCode}`),
      );

      if (restrictedProductTag) {
        const restrictedProductId = restrictedProductTag.split(
          `shipping-restricted-replacement|${window.shippingCode}|`,
        )[1];
        try {
          const response = await storefront.request(
            gqlQueries.productAvailabilityByVariantId,
            {
              id: `gid://shopify/ProductVariant/${restrictedProductId}`,
            },
          );
          return !response.node;
        } catch (error) {
          console.error('Error fetching product availability:', error);
          // Handle error accordingly, e.g., return false or throw error
          return false;
        }
      }
    }
    return false;
  };

  window.KENDO.handleProductCardClick = async (
    product,
    isQuickShop,
  ) => {
    let index = 0;
    const productCardElements = document.querySelectorAll('.ss__result--item');
    productCardElements.forEach((productCard, idx) => {
      if (productCard.querySelector(`[variant-id="${product.attributes.ss_id}"]`)) {
        index = idx;
      };
    });

    const listData = {
      listName:
        window.searchspring.controller?.search?.context?.collection?.name ||
        'Search',
      listId:
        window.searchspring.controller?.search?.context?.collection?.handle ||
        'search',
      index: index + 1,
      moduleName: 'collection page'
    };

    if (isQuickShop) {
      listData.click_text = 'quick_shop';
    }

    const productResponse = await storefront.request(gqlQueries.product, {
      handle: product.attributes.handle,
    });
    const formatted = convertGQLToProductJson(productResponse.product);
    const otherData = {
      origin: document.body.dataset.origin,
      originProduct: document.body.dataset.originProduct,
      index: index + 1,
    };
    if (formatted.isBundle || formatted.bundled) {
      otherData.bundleName = formatted.title;
      otherData.bundleId = formatted.id;
      listData.bundleName = formatted.title;
      listData.bundleId = formatted.id;
    }
    otherData.variant = formatted.variants[0];

    const itemObject = buildItemTrackingData(formatted, 1, otherData);
    let itemArray = [itemObject];

    if (formatted.isBundle || formatted.bundled) {
      const bundleProductHandles =
        formatted.metafields.accentuate.bundled_products.map(
          (handle) => handle.split('|')[0],
        );
      itemArray = await Promise.all(
        bundleProductHandles.map(async (handle) => {
          const bundleResponse = await storefront.request(gqlQueries.product, {
            handle,
          });
          const formatted = convertGQLToProductJson(bundleResponse.product);
          formatted.isBundle = true;
          otherData.variant = null;
          const itemObject = buildItemTrackingData(formatted, 1, otherData);
          return itemObject;
        }),
      );
    }

    setLocalStorageProperties(otherData, listData);
    trackProductClick('select_item', itemArray, listData);

    if(formatted.type == 'Bundle Builder') {
      trackEvent({
        event: 'select_bundle',
        site_section: window.__GLOBAL__.siteSection,
        component_name: 'Bundle Builder PLP Product Card',
        item_list_id: listData.listId,
        item_list_name: listData.listName,
        bundle_type: 'bundle builder',
        bundle_name: htmlDecode(formatted.title),
        bundle_id: String(formatted.id),
        price: formatted.price / 100,
        currency: window.Shopify.currency.active,
        index: listData.index
      });
    }
  };

  window.KENDO.handleWishlistToggle = async (
    product,
    isInWishlist = false,
    index = 0,
  ) => {
    const productResponse = await storefront.request(gqlQueries.product, {
      handle: product.attributes.handle,
    });
    const formatted = convertGQLToProductJson(productResponse.product);
    const otherData = {
      listName:
        window.searchspring.controller?.search?.context?.collection?.name ||
        'Search',
      listId:
        window.searchspring.controller?.search?.context?.collection?.handle ||
        'search',
      origin: document.body.dataset.origin,
      originProduct: document.body.dataset.originProduct,
      index: index + 1,
    };

    if (formatted.isBundle || formatted.bundled) {
      otherData.bundleName = formatted.title;
      otherData.bundleId = formatted.id;
    }

    const itemObject = buildItemTrackingData(formatted, 1, otherData);
    let itemArray = [itemObject];

    if (formatted.isBundle || formatted.bundled) {
      const bundleProductHandles =
        formatted.metafields.accentuate.bundled_products.map(
          (handle) => handle.split('|')[0],
        );
      itemArray = await Promise.all(
        bundleProductHandles.map(async (handle) => {
          const bundleResponse = await storefront.request(gqlQueries.product, {
            handle,
          });
          const formatted = convertGQLToProductJson(bundleResponse.product);
          formatted.isBundle = true;
          const itemObject = buildItemTrackingData(formatted, 1, otherData);
          return itemObject;
        }),
      );
    }

    const ecommerce = {
      currency: itemObject.currency,
      value: itemObject.price,
      items: itemArray,
    };

    if (formatted.isBundle || formatted.bundled) {
      ecommerce.bundle_name = htmlDecode(formatted.title);
      ecommerce.bundle_id = String(formatted.id);
    }

    dataLayer.push({
      event: isInWishlist ? 'add_to_wishlist' : 'remove_from_wishlist',
      ecommerce,
    });
  };

  window.convertPrice = (price) => {
    return new Promise((resolve) => {
      resolve({ ConvertedPrice: price });
    });
  };
};

/**
 * Here, we take all of the independent
 * Vuex modules laid out in the store
 * subdirectories and create a single Vuex
 * store that includes all of them.
 */
export default new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  state: {},
  mutations: {
    setCart(state, status) {
      state.open = status;
    },
    setModalTitle(state, payload) {
      state.modalTitle = payload;
    },
    setCtaText(state, payload) {
      state.ctaText = payload;
    },
    setProduct(state, data) {
      state.product = data;
    },
    openModal(state, data) {
      state.global.modal.current = data;
    },
  },
  modules: {
    global,
    cart,
    product,
    quickView,
    shadeFinder,
    vto,
    // As you build out other state-backed areas
    // of the site, add new Vuex modules here. If you have
    // a complicated PLP or PDP collection of components,
    // you may want to add a Vuex module for it!
  },
  plugins: [defineGlobals],
});
